import { useCallback, useReducer } from 'react';
import * as yup from 'yup';

import type { ModalLayerProps } from './components/_ModalLayer';

export interface ModalConfig {
  size?: ModalLayerProps['size'];
  isSide?: boolean;
  hasCloseButton?: boolean;
  hasDefaultPadding?: boolean;
  isOverlayClickable?: boolean;
}

export type ModalPayload =
  | [JSX.Element, ModalConfig]
  | [JSX.Element]
  | null
  | undefined;

export interface ModalItem extends ModalConfig {
  el: JSX.Element;
}

export type StackReducer<T, K> = (stack: T[], item: K) => T[];

const modalConfigSchema = yup.object({
  size: yup.string().required(),
  isSide: yup.boolean().required(),
  hasCloseButton: yup.boolean().required(),
  hasDefaultPadding: yup.boolean().required(),
  isOverlayClickable: yup.boolean().required(),
});

export const DEFAULT_MODAL_CONFIG: Required<ModalConfig> = {
  size: 'md',
  isSide: false,
  hasCloseButton: false,
  hasDefaultPadding: true,
  isOverlayClickable: false,
};

export default function useModal() {
  /**
   * 렌더링할 엘리먼트 스택
   */
  const [stack, pushItem] = useReducer<StackReducer<ModalItem, ModalPayload>>(
    /**
     * 리듀서 함수 중복 호출로 인한 이슈 참고
     * @issue https://github.com/facebook/react/issues/16295#issuecomment-641867291
     */
    useCallback((stack, item) => {
      const hasCustomConfig = Boolean(item?.[1]);

      if (item) {
        if (!hasCustomConfig) {
          stack.push({ el: item[0], ...DEFAULT_MODAL_CONFIG });
        } else if (!modalConfigSchema.isValidSync(item[1])) {
          stack.push({ el: item[0], ...DEFAULT_MODAL_CONFIG, ...item[1] });
        } else {
          stack.push({ el: item[0], ...item[1] });
        }
      } else {
        stack.pop();
      }

      return [...stack];
    }, []),
    []
  );

  /**
   * 렌더링 트리거에 사용할 모달 비활성화 메서드
   */
  const popItem = useCallback(() => pushItem(null), []);

  const clearItems = useCallback(() => {
    stack.forEach(() => popItem());
  }, [stack]);

  return { stack, pushItem, popItem, clearItems };
}
